import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { Box, Button, Typography, Snackbar, Alert, Grid2, Paper, Stack } from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import PromotionsDialog from "../component/PromotionsDialog";
import { useQuery, useMutation } from "@tanstack/react-query";
import ViewCartTable from "../component/ViewCartTableEposSales";
import { fetchPlaceOrder, checkProductPromotion } from "../../../../data/epos";
import { transformCartToPromotionPayload, mergePromotionResponseWithCart, transformProductDetailsToCart } from "../utils/promotionHelpers";
import { determineItemType, computeTelecallerVatBreakdownTotals, getExVatGoodsValueForTotals, isRhpEposTenant } from "../../Epos/utils/cartCalculations";
import CartSummaryEposSales from "../../Epos/component/CartSummaryEposSales";
import CustomerSearch, { formatCustomerDisplayLabel } from "../../Epos/component/CustomerSearch";
import ProductSearch from "../../Epos/component/ProductSearch";
import config from "../../../common/Config";
import "../../../common/commonStyles.css";

const ViewEposSales = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const role = String(localStorage.getItem("user_role") || "").trim();
        if (isRhpEposTenant() && role === "18") {
            navigate('/sales/epos_sales', { replace: true });
        }
    }, [navigate]);

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [orderHeader, setOrderHeader] = useState(null);
    const [orderChannel, setOrderChannel] = useState("pos");
    const [totalInvDiscPercent, setTotalInvDiscPercent] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
    const [productSearchInput, setProductSearchInput] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [customerSearchInput, setCustomerSearchInput] = useState("");
    const [cartItems, setCartItems] = useState([]);
    const cartItemsRef = useRef([]);
    const [originalOrderProducts, setOriginalOrderProducts] = useState([]);
    const [customerId, setCustomerId] = useState(null);
    const [promotionSummary, setPromotionSummary] = useState(null);
    const [bundleDiscounts, setBundleDiscounts] = useState([]);
    const [cartTotals, setCartTotals] = useState({
        boxCount: 0,
        pieceCount: 0,
        subtotal: 0,
        vat: 0,
        stdGoods: 0,
        zeroGoods: 0,
        totalBeforeDiscount: 0,
        discountAmount: 0,
        promoDiscPercent: 0,
        promoDisc: 0,
        promoVat: 0,
        vatExcludingPromo: 0,
        grandTotal: 0,
        gross: 0
    });
    const [promotionsModalOpen, setPromotionsModalOpen] = useState(false);
    const [apiTotals, setApiTotals] = useState(null);
    const customerSearchRef = useRef(null);
    const productSearchRef = useRef(null);
    const tillUserId = localStorage.getItem('tillUserId');

    const isTelecaller = useMemo(() => {
        return localStorage.getItem("user_role") === "20";
    }, []);

    const { data: orderData, isLoading } = useQuery({
        queryKey: ["viewEposOrder", orderId],
        queryFn: () => fetchPlaceOrder(orderId),
        enabled: !!orderId,
    });

    const promotionChannel = useMemo(() => {
        const userRole = localStorage.getItem("user_role");
        if (userRole === "20") {
            return "5";
        } else if (userRole === "18") {
            return "4";
        }
        return orderChannel || "pos";
    }, [orderChannel]);

    const checkPromotionMutation = useMutation({
        mutationFn: checkProductPromotion,
        onSuccess: (data) => {
            if (data?.success && data?.data?.items) {
                const currentCartItems = cartItemsRef.current;
                const cartDataForMerge = {
                    data: currentCartItems.length > 0
                        ? currentCartItems
                        : transformProductDetailsToCart(originalOrderProducts),
                    success: true
                };
                const mergedItems = mergePromotionResponseWithCart(cartDataForMerge, data, originalOrderProducts, currentCartItems);
                setCartItems(mergedItems);

                if (data?.data?.totals) {
                    setApiTotals(data.data.totals);
                }

                if (Array.isArray(data?.data?.bundle_discounts)) {
                    setBundleDiscounts(data.data.bundle_discounts);
                } else {
                    setBundleDiscounts([]);
                }

                if (data?.data?.promotion_summary) {
                    setPromotionSummary(data.data.promotion_summary);
                } else if (data?.data?.applied_promotions && Array.isArray(data.data.applied_promotions)) {
                    setPromotionSummary({
                        applied_promotions: data.data.applied_promotions,
                        available_promotions: data.data.available_promotions || data.data.applicable_promotions || []
                    });
                } else {
                    setPromotionSummary(null);
                }
            } else {
                setCartItems([]);
                setApiTotals(null);
                setBundleDiscounts([]);
                setPromotionSummary(null);
            }
        },
        onError: (error) => {
            setSnackbar({ open: true, message: "Failed to check promotions", severity: "error" });
            setCartItems([]);
            setApiTotals(null);
            setBundleDiscounts([]);
        },
    });

    const getItemDetails = useCallback((item) => {
        return determineItemType(item);
    }, []);

    const calculateCartTotals = useCallback(() => {
        const bundleList = Array.isArray(bundleDiscounts) ? bundleDiscounts : [];

        if (isTelecaller && cartItems && cartItems.length > 0) {
            let boxCount = 0;
            let pieceCount = 0;

            cartItems.forEach((item) => {
                const orderType = item.type || item.order_type || "Box";
                const qty = parseInt(item.qty) || 0;
                if (orderType === "Box") {
                    boxCount += qty;
                } else {
                    pieceCount += qty;
                }
            });

            const telecallerTotals = computeTelecallerVatBreakdownTotals(cartItems, bundleList);

            setCartTotals({
                boxCount,
                pieceCount,
                subtotal: telecallerTotals.stdGoods + telecallerTotals.zeroGoods,
                vat: telecallerTotals.vat,
                stdGoods: telecallerTotals.stdGoods,
                zeroGoods: telecallerTotals.zeroGoods,
                totalBeforeDiscount: telecallerTotals.grandTotal,
                discountAmount: 0,
                promoDiscPercent: 0,
                promoDisc: 0,
                promoVat: 0,
                vatExcludingPromo: telecallerTotals.vat,
                grandTotal: telecallerTotals.grandTotal,
                gross: 0,
            });
            return;
        }

        let bundlePromoDiscount = 0;
        let bundlePromoVat = 0;
        if (cartItems && cartItems.length > 0) {
            bundleList.forEach((b) => {
                bundlePromoDiscount += parseFloat(b.buy_total_discount || 0) + parseFloat(b.get_total_discount || 0);
                bundlePromoVat += parseFloat(b.buy_discount_vat || 0) + parseFloat(b.get_discount_vat || 0);
            });
        }

        if (apiTotals && cartItems.length > 0 && cartItems.some(item => item.useBackendCalculations)) {
            let boxCount = 0;
            let pieceCount = 0;
            let stdGoods = 0;
            let zeroGoods = 0;
            let freePromoLineVatSum = 0;

            cartItems.forEach((item) => {
                const orderType = item.type || item.order_type || "Box";
                const qty = parseInt(item.qty) || 0;

                if (orderType === "Box") {
                    boxCount += qty;
                } else {
                    pieceCount += qty;
                }

                const vatRate = parseFloat(item.rate || item.tax_rate || 0);
                const itemSubtotalForGoods = getExVatGoodsValueForTotals(item);

                const promoTypeNorm = String(item.promo_type || item.promoType || "").trim().toUpperCase();
                const isPromoFreeLine = promoTypeNorm === "FREE";
                if (isPromoFreeLine) {
                    const lv = parseFloat(item.line_vat || item.line_tax || item.vat || item.tax || 0);
                    if (Number.isFinite(lv)) {
                        freePromoLineVatSum += lv;
                    }
                }
                if (!isPromoFreeLine && itemSubtotalForGoods > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotalForGoods;
                    } else {
                        zeroGoods += itemSubtotalForGoods;
                    }
                }
            });

            const backendSubtotal = parseFloat(apiTotals.subtotal || apiTotals.sub_total || 0);
            const backendVatRaw = parseFloat(apiTotals.vat || apiTotals.tax || 0);
            const backendVat = Math.max(0, backendVatRaw - (Number.isFinite(freePromoLineVatSum) ? freePromoLineVatSum : 0));
            const gross = parseFloat((stdGoods + zeroGoods + backendVat).toFixed(2));

            const totalInvoiceDiscountAmount = (gross * totalInvDiscPercent) / 100;
            const hasBundlePromoVat = Number.isFinite(bundlePromoVat) && bundlePromoVat > 0.0001;
            const vatExcludingPromo = hasBundlePromoVat
                ? parseFloat((backendVat - bundlePromoVat).toFixed(2))
                : 0;
            const promoVatAdjust = hasBundlePromoVat ? bundlePromoVat : 0;
            const localGrand = Math.max(0, gross - bundlePromoDiscount - promoVatAdjust);
            const apiGrandParsed = parseFloat(apiTotals.grand_total ?? apiTotals.grandTotal ?? NaN);
            const hasLineItemDiscount = cartItems.some((it) => {
                const d = parseFloat(it.discount ?? it.item_discount ?? it.manualOverrides?.discount ?? 0);
                return Number.isFinite(d) && d > 0.0001;
            });
            const grandTotal = hasLineItemDiscount
                ? localGrand
                : Number.isFinite(apiGrandParsed)
                    ? Math.max(0, apiGrandParsed)
                    : localGrand;

            setCartTotals({
                boxCount,
                pieceCount,
                subtotal: backendSubtotal,
                vat: backendVat,
                stdGoods,
                zeroGoods,
                totalBeforeDiscount: gross,
                discountAmount: totalInvoiceDiscountAmount,
                promoDiscPercent: 0,
                promoDisc: bundlePromoDiscount,
                promoVat: bundlePromoVat,
                vatExcludingPromo,
                grandTotal,
                gross
            });
            return;
        }

        let boxCount = 0;
        let pieceCount = 0;
        let stdGoods = 0;
        let zeroGoods = 0;
        let subtotalAmount = 0;
        let vatAmount = 0;

        cartItems.forEach((item) => {
            const orderType = item.type || item.order_type || "Box";
            const qty = parseInt(item.qty) || 0;

            if (orderType === "Box") {
                boxCount += qty;
            } else {
                pieceCount += qty;
            }

            if (item.useBackendCalculations) {
                const taxMethod = item.tax_method || "exclusive";
                const isVatInclusive = taxMethod === "inclusive";
                const lineSubtotal = parseFloat(item.line_subtotal || item.subtotal || item.sub_total || 0);
                const lineVat = parseFloat(item.line_vat || item.line_tax || item.vat || item.tax || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);

                const promoTypeNorm = String(item.promo_type || item.promoType || "").trim().toUpperCase();
                const isPromoFreeLine = promoTypeNorm === "FREE";

                if (!isPromoFreeLine) {
                    subtotalAmount += lineSubtotal;
                    vatAmount += lineVat;
                } else if (isVatInclusive) {
                    const net = lineSubtotal - lineVat;
                    subtotalAmount += Number.isFinite(net) && net > 0 ? net : 0;
                } else {
                    subtotalAmount += lineSubtotal;
                }

                const itemSubtotalForGoods = isVatInclusive ? (lineSubtotal - lineVat) : lineSubtotal;
                if (!isPromoFreeLine && itemSubtotalForGoods > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotalForGoods;
                    } else {
                        zeroGoods += itemSubtotalForGoods;
                    }
                }
            } else {
                const price = parseFloat(item.price) || 0;
                const discount = parseFloat(item.discount || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);
                const taxMethod = item.tax_method || "exclusive";
                const isVatInclusive = taxMethod === "inclusive";

                const itemDiscountType = item.discount_type || "amount";
                const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
                const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

                const promoTypeNorm = String(item.promo_type || item.promoType || "").trim().toUpperCase();
                const isPromoFreeLine = promoTypeNorm === "FREE";

                let baseTotal = price * qty;

                let discountedTotal;
                if (isFixedDiscount) {
                    const discountAmount = parseFloat(discount) || 0;
                    discountedTotal = Math.max(0, baseTotal - discountAmount);
                } else {
                    const multiplier = Math.min(Math.max(discount, 0), 100);
                    discountedTotal = baseTotal * (1 - multiplier / 100);
                }

                let itemSubtotal;
                let itemVat;
                if (isVatInclusive && vatRate > 0) {
                    itemSubtotal = discountedTotal / (1 + vatRate / 100);
                    itemVat = discountedTotal - itemSubtotal;
                } else {
                    itemSubtotal = discountedTotal;
                    itemVat = itemSubtotal > 0 ? (itemSubtotal * vatRate) / 100 : 0;
                }

                if (isPromoFreeLine) {
                    itemVat = 0;
                }

                subtotalAmount += itemSubtotal;
                vatAmount += itemVat;

                if (!isPromoFreeLine && itemSubtotal > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotal;
                    } else {
                        zeroGoods += itemSubtotal;
                    }
                }
            }
        });

        const gross = parseFloat((stdGoods + zeroGoods + vatAmount).toFixed(2));
        const totalBeforeDiscount = gross;
        const totalInvoiceDiscountAmount = (gross * totalInvDiscPercent) / 100;
        const grandTotalBeforeBundle = gross;
        const hasBundlePromoVatLocal = Number.isFinite(bundlePromoVat) && bundlePromoVat > 0.0001;
        const vatExcludingPromo = hasBundlePromoVatLocal
            ? parseFloat((vatAmount - bundlePromoVat).toFixed(2))
            : 0;
        const promoVatAdjust = hasBundlePromoVatLocal ? bundlePromoVat : 0;
        const apiGrandFallback = parseFloat(apiTotals?.grand_total ?? apiTotals?.grandTotal ?? NaN);
        const grandTotal = Number.isFinite(apiGrandFallback)
            ? Math.max(0, apiGrandFallback)
            : Math.max(0, grandTotalBeforeBundle - bundlePromoDiscount - promoVatAdjust);

        setCartTotals({
            boxCount,
            pieceCount,
            subtotal: subtotalAmount,
            vat: vatAmount,
            stdGoods,
            zeroGoods,
            totalBeforeDiscount,
            discountAmount: totalInvoiceDiscountAmount,
            promoDiscPercent: 0,
            promoDisc: bundlePromoDiscount,
            promoVat: bundlePromoVat,
            vatExcludingPromo,
            grandTotal,
            gross
        });
    }, [cartItems, totalInvDiscPercent, apiTotals, bundleDiscounts, isTelecaller]);

    const noOpHandler = useCallback(() => {}, []);

    const extractDateAndTime = (dateTimeString) => {
        if (!dateTimeString) return { date: "", time: "" };
        const dateObj = new Date(dateTimeString);
        const date = dateObj.toISOString().split('T')[0];
        const time = dateObj.toTimeString().split(' ')[0];
        return { date, time };
    };

    useEffect(() => {
        if (!orderData) return;
        if (!orderData?.success) {
            setSnackbar({ open: true, message: orderData?.message || "Failed to load order", severity: "error" });
            return;
        }
        const details = orderData?.details || {};
        const order = Array.isArray(details?.orderDetails) ? details.orderDetails[0] : null;
        const products = Array.isArray(details?.product_details) ? details.product_details : [];

        setOrderHeader(order || null);
        const orderAccountNo = order?.accound_no ?? order?.account_no ?? "";
        const orderCustomer = order ? {
            id: order?.customer_id,
            name: order?.name,
            company: order?.company || "",
            address: order?.delivery_address || order?.address || "",
            accound_no: orderAccountNo,
            account_no: orderAccountNo,
        } : null;
        setSelectedCustomer(orderCustomer);
        if (order) {
            setCustomerSearchInput(formatCustomerDisplayLabel(orderCustomer));
        }

        if (order?.customer_id) {
            setCustomerId(order.customer_id);
        }

        if (order?.channel) {
            setOrderChannel(order.channel);
        }

        setOriginalOrderProducts(products);

        const orderDiscount = Number(order?.order_discount ?? 0) || 0;
        setTotalInvDiscPercent(orderDiscount);
    }, [orderData]);
    
    useEffect(() => {
        if (!customerId) return;

        if (!originalOrderProducts?.length) {
            setSnackbar({ open: true, message: "No products found for this order", severity: "info" });
            setCartItems([]);
            return;
        }

        const transformedProducts = transformProductDetailsToCart(originalOrderProducts);
        setCartItems([]);

        const cartDataToUse = {
            data: transformedProducts,
            success: true
        };

        const { date: orderDate, time: orderTime } = extractDateAndTime(orderHeader?.date);
        const orderId = orderHeader?.order_id ?? orderHeader?.id ?? null;
        const payload = transformCartToPromotionPayload(cartDataToUse, customerId, orderDate, promotionChannel, orderTime, orderId);
        checkPromotionMutation.mutate(payload);
    }, [customerId, originalOrderProducts, orderHeader, promotionChannel]);

    useEffect(() => {
        cartItemsRef.current = cartItems;
    }, [cartItems]);

    useEffect(() => {
        if (cartItems.length !== 0) return;
        setPromotionSummary(null);
        setBundleDiscounts([]);
        setApiTotals(null);
    }, [cartItems.length]);

    useEffect(() => {
        calculateCartTotals();
    }, [calculateCartTotals]);

    const formatPromotionDate = (dateString) => {
        if (!dateString || dateString === 'N/A') return dateString;
        try {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            if (dateString.includes('/')) {
                const [day, month, year] = dateString.split('/');
                const date = new Date(year, month - 1, day);
                return `${day}-${monthNames[date.getMonth()]}`;
            }
            if (dateString.includes('-')) {
                const date = new Date(dateString);
                if (!isNaN(date.getTime())) {
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${day}-${monthNames[date.getMonth()]}`;
                }
            }
            return dateString;
        } catch {
            return dateString;
        }
    };

    const getPromotionBanner = (promotion) => {
        if (promotion.type_id === "1") return "Buy X Get Y Free";
        if (promotion.type_id === "2") return "Buy X Get Y Discount";
        if (promotion.type_id === "3") return "Buy X at Discount";
        if (promotion.type_id === "4") return "Multi Layer Special";
        if (promotion.type_id === "5") return "Group Offer";
        return promotion.type || "Special Offer";
    };

    const transformAPIPromotionSummary = (promo) => {
        const buyProducts = (promo.buy_products || []).filter(p => p.product_name && p.product_name.trim() !== '');
        const offerProducts = (promo.get_products || promo.offer_products || []).filter(p => p.product_name && p.product_name.trim() !== '');
        const allProducts = [...buyProducts, ...offerProducts];
        const productsList = allProducts.length > 0
            ? allProducts.map(p => p.product_name || `${p.product_code || ''} - ${p.product_name || ''}`)
            : (promo.products || []);
        const validity = promo.start_date && promo.end_date
            ? `${formatPromotionDate(promo.start_date)} to ${formatPromotionDate(promo.end_date)}`
            : (promo.validity || 'N/A');
        const typeLabel = getPromotionBanner(promo);
        return {
            id: String(promo.promotion_id || promo.id || ''),
            title: promo.name || promo.title || 'Promotion',
            description: promo.description || `${promo.type || ''} - Savings: £${(promo.savings || 0).toFixed(2)}`,
            validity,
            products: productsList,
            banner: typeLabel,
            buyProducts,
            offerProducts,
            rawData: promo,
            savings: promo.savings || 0,
            times_eligible: promo.times_eligible || 1
        };
    };

    const appliedPromotionsFromAPI = (promotionSummary?.applied_promotions || []).map(transformAPIPromotionSummary);
    const availablePromotionsFromAPI = (promotionSummary?.available_promotions || []).map(transformAPIPromotionSummary);
    const cartHasLines = cartItems.length > 0;
    const promotionCount = cartHasLines ? appliedPromotionsFromAPI.length : 0;
    const totalApplicable = cartHasLines
        ? appliedPromotionsFromAPI.length + availablePromotionsFromAPI.length
        : 0;

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handlePrintDeliveryNote = (id) => {
        if (id) {
            const accessToken = localStorage.getItem('accessToken');
            if (accessToken === 'rhp' || accessToken === 'hp') {
                window.open(`${config.BASE_URL}/invoice/rhp_delivery_note.html?orderId=${id}`, '_blank', 'noopener,noreferrer');
            } else {
                window.open(`${config.BASE_URL}/invoice/sales_invoice.html?orderId=${id}&prefix=${encodeURIComponent(accessToken || '')}`, '_blank', 'noopener,noreferrer');
            }
        }
    };

    const handlePrintDrinkSheet = (id) => {
        if (id) {
            const accessToken = localStorage.getItem('accessToken');
            if (accessToken === 'rhp' || accessToken === 'hp') {
                window.open(`${config.BASE_URL}/invoice/rhp_drink_sheet.html?orderId=${id}`, '_blank', 'noopener,noreferrer');
            } else {
                window.open(`${config.BASE_URL}/invoice/sales_invoice.html?orderId=${id}&prefix=${encodeURIComponent(accessToken || '')}`, '_blank', 'noopener,noreferrer');
            }
        }
    };

    const isLoadingData = isLoading;

    const customerOptions = useMemo(() => {
        return [];
    }, []);

    const productOptions = useMemo(() => {
        return [];
    }, []);

    const isRhp = useMemo(() => {
        const accessToken = localStorage.getItem('accessToken');
        return accessToken === 'rhp' || accessToken === 'hp';
    }, []);

    return (
        <>
            <Box sx={{ p: 1, backgroundColor: '#f5f7fa' }}>
                {isLoadingData && (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                        <Typography>Loading order details...</Typography>
                    </Box>
                )}
                {!isLoadingData && (
                    <Grid2 container spacing={1} sx={{ overflowX: 'hidden' }}>
                        <Grid size={{ xs: 12, md: 9 }} paper elevation={2} bgcolor="#ffffff" borderRadius={2} sx={{ px: 2,pt: 2, pb: 1, overflowX: 'hidden' }}>
                            <Grid2 container spacing={2}
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row", md: "row" },
                                    justifyContent: "space-between",
                                    alignItems: { xs: "flex-start", sm: "flex-start", md: "flex-end" },
                                    gap: 2,
                                }}
                            >
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <CustomerSearch
                                        ref={customerSearchRef}
                                        selectedCustomer={selectedCustomer}
                                        onCustomerChange={noOpHandler}
                                        searchInput={customerSearchInput}
                                        onSearchInputChange={noOpHandler}
                                        options={customerOptions}
                                        isLoading={false}
                                        debouncedSearch=""
                                        disabled={true}
                                        disabledTextColor="#000"
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12, md: 6 }} display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                                    <Box sx={{ flex: 1, opacity: 0.6 }}>
                                        <ProductSearch
                                            ref={productSearchRef}
                                            selectedProduct={null}
                                            onProductChange={noOpHandler}
                                            searchInput=""
                                            onSearchInputChange={noOpHandler}
                                            options={[]}
                                            isLoading={false}
                                            selectedCustomer={null}
                                            debouncedSearch=""
                                        />
                                    </Box>
                                    {/* <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", whiteSpace: "nowrap", fontWeight: 500 }}>
                                        View Only - Cannot Add Products
                                    </Typography> */}
                                </Grid2>
                            </Grid2>

                            <Grid size={{ xs: 12, md: 12 }}>
                                <ViewCartTable
                                    cartItems={cartItems}
                                    showVatAsAmount={true}
                                    getItemDetails={getItemDetails}
                                    isLoading={checkPromotionMutation.isPending}
                                    isTelecaller={isTelecaller}
                                    bundleDiscounts={bundleDiscounts}
                                />
                                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: 1,justifyContent: { xs: "center", md: "flex-end" }, borderTop: "1px solid #E0E0E0",pt: 1 }}>
                          
                                    {isRhp ? (
                                        <> 
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        sx={{
                                            fontSize: "0.8rem",
                                            fontWeight: 500,
                                            '&.MuiButton-contained': { height: "32px" },
                                            width: { xs: "100%", md: "auto" },
                                            backgroundColor: "#F8991D",
                                            "&:hover": {
                                                backgroundColor: "#F8991D",
                                                boxShadow: "none",
                                            },
                                        }}
                                        onClick={() => handlePrintDeliveryNote(orderId)}>
                                        Print Delivery Note
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        sx={{
                                            fontSize: "0.8rem",
                                            fontWeight: 500,
                                            '&.MuiButton-contained': { height: "32px" },
                                            width: { xs: "100%", md: "auto" },
                                            backgroundColor: "#F8991D",
                                            "&:hover": {
                                                backgroundColor: "#F8991D",
                                                boxShadow: "none",
                                                color: "#fff",
                                            },
                                        }}
                                        onClick={() => handlePrintDrinkSheet(orderId)}>
                                        Print Drink Sheet
                                    </Button>
                                    </>)
                                    : null}
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid2 size={{ xs: 12, md: 3 }} paper elevation={2} bgcolor="#ffffff" borderRadius={2} sx={{ p: 2, overflowX: 'hidden' }}>
                            <CartSummaryEposSales
                                cartTotals={cartTotals}
                                totalInvDiscPercent={totalInvDiscPercent}
                                onTotalInvDiscChange={noOpHandler}
                                isEditMode={false}
                                isTelecaller={isTelecaller}
                            />
              
                        </Grid2>
                    </Grid2>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ViewEposSales;
